import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import SectionHeader from './SectionHeader';
import SectionBlock from './SectionBlock';
import { ShouldRender } from '../../../../ui-core/ShouldRender';
import useInView from '../../../../hooks/useInView';
import { TEXT_CONSTANT } from '../../../../constants/app';

import './style.css';

const Next30Days = ({ backgroundColor, description, sections, text }) => {
  const sectionRef = useRef([]);
  const headerPositionRef = useRef(null);
  const addSectionRef = (element, index) => {
    sectionRef.current[index] = element;
  };
  const scrollTopValueRef = useRef(null);
  const wrapperRef = useRef(null);
  const inView = useInView(wrapperRef.current, undefined, false);
  const inViewRef = useRef(inView);
  const [activeIndex, setActiveIndex] = useState(0);
  const headerTopRef = useRef(null);

  useEffect(() => {
    inViewRef.current = inView;
    if (inView) {
      if (headerPositionRef.current && !headerTopRef.current) {
        const appElement = document.getElementById('app');
        headerTopRef.current =
          (appElement?.scrollTop || 0) +
          headerPositionRef.current.getBoundingClientRect().top;
      }
    }
  }, [inView]);

  function handleScroll(event) {
    if (headerPositionRef.current) {
      const { top } = headerPositionRef.current.getBoundingClientRect() || {};
      const height = headerPositionRef.current.offsetHeight;
      const scrollTop = event.target.scrollTop;

      const index = sectionRef.current.findIndex(ele => {
        const screenHeight = window.innerHeight;
        const { top } = ele.getBoundingClientRect();
        if (top > 0 && top < screenHeight) {
          return true;
        }
        return false;
      });

      if (index !== -1) {
        setActiveIndex(index);
      }

      if (inViewRef.current) {
        headerPositionRef.current.classList.remove('invisible');
      } else {
        headerPositionRef.current.classList.add('invisible');
      }

      if (scrollTopValueRef.current && scrollTopValueRef.current >= scrollTop) {
        scrollTopValueRef.current = null;
        headerPositionRef.current.classList.remove('sticky');
      } else if (top - height <= 0 && !scrollTopValueRef.current) {
        scrollTopValueRef.current = scrollTop;
        headerPositionRef.current.classList.add('sticky');
      }
    }
  }

  useEffect(() => {
    const appElement = document.getElementById('app');
    if (appElement) {
      appElement.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (appElement) {
        appElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleItemClick = index => {
    handleScrollToItem(index);
    setActiveIndex(index);
  };

  const updateScrollTop = () => {
    if (!scrollTopValueRef.current) {
      scrollTopValueRef.current = headerTopRef.current;
      headerPositionRef.current.classList.add('sticky');
    }
  };

  const handleScrollToItem = index => {
    const appElement = document.getElementById('app');
    const element = sectionRef.current[index];
    if (element) {
      if (element?.offsetTop && appElement) {
        const scrollY =
          element.offsetTop -
          (headerPositionRef.current.offsetHeight +
            (scrollTopValueRef.current ? 0 : 60));
        appElement.scroll(0, scrollY);
        updateScrollTop();
      } else {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  return (
    <div
      className="next-days"
      ref={wrapperRef}
      style={backgroundColor && { background: backgroundColor }}
    >
      <h2 className="text-center">{text || TEXT_CONSTANT.WHAT_YOU_GET}</h2>
      <ShouldRender check={!!description}>
        <p>{description}</p>
      </ShouldRender>
      <SectionHeader
        activeIndex={activeIndex}
        handleItemClick={handleItemClick}
        headerItems={sections.map(item => item.name)}
        ref={headerPositionRef}
        sections={sectionRef.current}
      />
      {sections.map((section, index) => (
        <SectionBlock
          key={`section-${index + 1}`}
          ref={element => addSectionRef(element, index)}
          section={section}
        />
      ))}
    </div>
  );
};

Next30Days.propTypes = {
  backgroundColor: PropTypes.string,
  description: PropTypes.string,
  sections: PropTypes.array.isRequired,
  text: PropTypes.string,
};

export default Next30Days;
